import { Box, Card, Checkbox, Dialog, FormControl, InputLabel, MenuItem, Button as MUIButton, Select, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import { ReactElement, useState } from 'react';
import {
  BulkDeleteButton,
  CreateButton,
  Datagrid,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useListController,
  useTranslate,
  DeleteButton,
  SelectField,
  TextInput,
  SelectInput,
  Button
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddressField from 'src/pages/admin/components/AddressField';
import CreateMetadata from './Create';
import EditMetadata from './Edit';
import { chains } from 'src/assets/data';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { Iconify } from 'src/components/iconify/iconify';
import { XButton } from 'src/pages/admin/components/XButton';


const ListFilter = (props: any) => {
  const { filterValues } = useListController();

  return (
    <Filter {...(props || {})}>
      {/* <FilterGroup alwaysOn spacing={0} p={0}>
        <FilterGroup>
          <TextInput source="address" label="module.tokenMetadata.address" />
          <SelectInput
              source="chainId"
              helperText={<span style={{ color: 'red' }}></span>}
              choices={[
                  { id: 501424, name: 'SOLANA' },
                  { id: 1, name: 'ETH' }
              ]}
          />
        </FilterGroup>
      </FilterGroup> */}
    </Filter>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  
  return (
    <PermissionGuard resource="token-metadatas" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.tokenMetadata')}
          links={[
            { name: translate('resource.token'), href: '#' },
            { name: translate('resource.tokenMetadata'), href: `/${Resources.tokenMetadata}` },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="token-metadatas" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateMetadata onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.tokenMetadata')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />
        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            filters={<ListFilter />}
            perPage={10}
            exporter={false}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={
                  <PermissionButton resource="token-metadatas" action="delete">
                    <BulkDeleteButton />
                  </PermissionButton>
                }
                sx={{
                  '.RaBulkActionsToolbar-toolbar': {
                    position: 'relative !important',
                    transform: 'none !important',
                  },
                }}
              >
                <TextField source="symbol" label="module.tokenMetadata.symbol" sortable={false} />
                <TextField source="name" label="module.tokenMetadata.name" sortable={false} />
                {/* <TextField source="address" label="module.tokenMetadata.address" /> */}
                <FunctionField
                  label="module.tokenMetadata.address"
                  render={(record: any) => <AddressField address={record.address} />}
                  sortable={false}
                />

                <SelectField
                  source="chainId"
                  choices={chains}
                  label="module.tokenMetadata.chain"
                  sortable={false}
                />

                <FunctionField
                  label="module.tokenMetadata.metadata"
                  textAlign="left"
                  minWidth={250}
                  sortable={false}
                  render={(record: any) => {
                    const { metadata } = record;
                    if (!metadata) return '';

                    const jsonString = JSON.stringify(metadata, null, 2);

                    return (
                      <Tooltip title={jsonString} arrow>
                        <Typography
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.875rem',
                          }}
                        >
                          {jsonString}
                        </Typography>
                      </Tooltip>
                    );
                  }}
                />

                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2}>
                      <PermissionGuard resource="token-metadatas" action="update" fallback={<></>}>
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditMetadata id={record.id} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        />
                      </PermissionGuard>
                      <PermissionGuard resource="token-metadatas" action="delete" fallback={<></>}>
                        <DeleteButton mutationMode="pessimistic" />
                      </PermissionGuard>
                      <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                        <XButton
                          variant="text"
                          size="medium"
                          color="secondary"
                          disabled={!record.hasAuditLog}
                          onClick={() => {
                            setSelectedRecord(record);
                            setAuditTitle(
                              `${translate('resource.auditLogs')} (#${record.id})`
                            );
                            setAuditLogOpen(true);
                          }}
                          label={translate('resource.auditLogs')}
                        >
                          <Iconify icon="ph:note" />
                        </XButton>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="md" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          recordId={selectedRecord?.id}
          resource={Resources.tokenMetadata}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
